/**
 * useSupabaseAuth - 統一的 Supabase 認證 Composable
 * 支援網頁版和原生 App (Capacitor) 的 Google OAuth 登入
 */

import { ref, type Ref } from 'vue';
import { useRouter, type Router } from 'vue-router';
import { supabase } from '@shared/services/core/supabaseClient';
import { authService, isTokenExpiredError, handleTokenExpiredError, withTokenRefresh } from '@shared/services/auth/authService';
import { Capacitor } from '@capacitor/core';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

/**
 * Google Auth 插件介面
 */
interface GoogleAuthPlugin {
    initialize: (config: GoogleAuthConfig) => Promise<void>;
    signIn: () => Promise<GoogleUser>;
    signOut: () => Promise<void>;
}

/**
 * Google Auth 配置介面
 */
interface GoogleAuthConfig {
    serverClientId: string;
    scopes: string[];
    grantOfflineAccess: boolean;
}

/**
 * Google 用戶資料介面
 */
interface GoogleUser {
    authentication?: {
        idToken?: string;
        accessToken?: string;
    };
    email?: string;
    displayName?: string;
    familyName?: string;
    givenName?: string;
    id?: string;
    imageUrl?: string;
}

/**
 * Capacitor 全域 Plugins 介面
 */
interface CapacitorPlugins {
    GoogleAuth?: GoogleAuthPlugin;
    [key: string]: unknown;
}

/**
 * 擴展 Window 介面以支援 Capacitor
 */
declare global {
    interface Window {
        Capacitor?: {
            Plugins?: CapacitorPlugins;
            isNativePlatform?: () => boolean;
        };
    }
}

// 在原生環境時靜態導入 GoogleAuth
// 這樣可以確保插件正確初始化
let GoogleAuthPlugin: GoogleAuthPlugin | null = null;

// 嘗試導入 GoogleAuth 插件（在模組載入時）
try {
    // 靜態導入，讓打包工具能正確處理
    import('@codetrix-studio/capacitor-google-auth').then((module: { GoogleAuth: GoogleAuthPlugin }) => {
        GoogleAuthPlugin = module.GoogleAuth;
    }).catch(() => {
        // 忽略 - 可能在網頁環境中
    });
} catch {
    // 忽略 - 可能在網頁環境中
}

/**
 * 檢查是否在原生 App 環境中運行
 */
const isNativePlatform = (): boolean => {
    try {
        return Capacitor.isNativePlatform();
    } catch {
        return false;
    }
};

/**
 * 取得 Google Auth 插件（僅在原生環境）
 */
const getGoogleAuthPlugin = (): GoogleAuthPlugin | null => {
    if (!isNativePlatform()) return null;

    if (GoogleAuthPlugin) {
        return GoogleAuthPlugin;
    }

    // 如果預載入失敗，嘗試從全域取得
    try {
        // Capacitor 插件通常會在 window.Capacitor.Plugins 中
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.GoogleAuth) {
            return window.Capacitor.Plugins.GoogleAuth;
        }
    } catch {
        // 忽略
    }

    return null;
};

/**
 * Supabase Auth 選項介面
 */
export interface UseSupabaseAuthOptions {
    /** 應用名稱（如 'chat', 'admin', 'ppom'） */
    appName?: string;
    /** OAuth 登入後的重定向路徑（相對於應用根目錄） */
    redirectPath?: string;
    /** 登入成功後的路由路徑 */
    successRedirectRoute?: string;
    /** 登入成功的回調函數 */
    onLoginSuccess?: ((user: User) => void | Promise<void>) | null;
    /** 登入前的處理函數 */
    beforeLogin?: (() => void | Promise<void>) | null;
    /** 檢查 session 後的處理函數 */
    afterSessionCheck?: ((session: Session) => void | Promise<void>) | null;
}

/**
 * Auth 訂閱介面
 */
export interface AuthSubscription {
    unsubscribe: () => void;
}

/**
 * useSupabaseAuth 回傳介面
 */
export interface UseSupabaseAuthReturn {
    /** 是否正在載入 */
    isLoading: Ref<boolean>;
    /** 錯誤訊息 */
    error: Ref<string | null>;
    /** 資訊訊息 */
    infoMessage: Ref<string>;
    /** 當前用戶 */
    user: Ref<User | null>;
    /** 使用 Google OAuth 登入 */
    signInWithGoogle: () => Promise<void>;
    /** 登出 */
    signOut: () => Promise<void>;
    /** 檢查當前登入狀態 */
    checkLoginStatus: () => Promise<User | null>;
    /** 檢查並處理 OAuth 回調 */
    checkAuthCallback: () => Promise<User | null>;
    /** 初始化認證 */
    initAuth: () => Promise<AuthSubscription>;
    /** 清理 URL 參數 */
    cleanupUrlParams: () => void;
}

/**
 * 統一的 Supabase 認證 composable
 * @param options - 配置選項
 * @returns 認證相關的響應式資料和方法
 */
export function useSupabaseAuth(options: UseSupabaseAuthOptions = {}): UseSupabaseAuthReturn {
    const {
        appName = 'app',
        redirectPath = '/',
        successRedirectRoute = '/',
        onLoginSuccess = null,
        beforeLogin = null,
        afterSessionCheck = null
    } = options;

    const router: Router = useRouter();
    const isLoading = ref<boolean>(false);
    const error = ref<string | null>(null);
    const infoMessage = ref<string>('');
    const user = ref<User | null>(null);

    /**
     * 使用 Google OAuth 登入（自動判斷網頁或原生環境）
     */
    const signInWithGoogle = async (): Promise<void> => {
        try {
            isLoading.value = true;
            error.value = null;

            // 執行登入前的處理
            if (beforeLogin) {
                await beforeLogin();
            }

            // 判斷是否在原生 App 環境
            const isNative = isNativePlatform();

            if (isNative) {
                await signInWithGoogleNative();
            } else {
                await signInWithGoogleWeb();
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '登入失敗，請稍後再試';
            console.error(`[${appName}] 登入失敗:`, errorMessage);
            error.value = errorMessage;
            infoMessage.value = '';
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * 網頁版 Google OAuth 登入（使用重新導向）
     */
    const signInWithGoogleWeb = async (): Promise<void> => {
        infoMessage.value = '正在跳轉到 Google 登入頁面...';

        // 構建完整的重定向 URL
        const fullRedirectUrl = `${window.location.origin}${redirectPath}`;

        const { error: authError } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: fullRedirectUrl,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                }
            }
        });

        if (authError) throw authError;

        // OAuth 會自動重定向，這裡不需要額外處理
    };

    /**
     * 原生 App Google 登入（使用 Capacitor 插件）
     */
    const signInWithGoogleNative = async (): Promise<void> => {
        infoMessage.value = '正在開啟 Google 登入...';

        // 取得 GoogleAuth 插件
        const GoogleAuth = getGoogleAuthPlugin();
        if (!GoogleAuth) {
            throw new Error('Google 登入模組未載入，請稍後再試或重新開啟 App');
        }

        // 初始化 GoogleAuth（如果尚未初始化）
        try {
            await GoogleAuth.initialize({
                serverClientId: '219264788569-o41chdtn0hc5l87o4v4t5uiusmnascbk.apps.googleusercontent.com',
                scopes: ['profile', 'email'],
                grantOfflineAccess: true
            });
        } catch {
            // 可能已經初始化過，忽略錯誤
        }

        // 執行原生 Google 登入
        const googleUser: GoogleUser = await GoogleAuth.signIn();

        // 取得 ID Token
        const idToken = googleUser.authentication?.idToken;
        if (!idToken) {
            throw new Error('無法取得 Google ID Token');
        }

        // 使用 ID Token 登入 Supabase
        infoMessage.value = '正在驗證身份...';

        const { data, error: authError } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: idToken
        });

        if (authError) {
            throw authError;
        }

        // 設置用戶
        user.value = data.user;

        // 執行 session 檢查後的處理
        if (afterSessionCheck && data.session) {
            await afterSessionCheck(data.session);
        }

        infoMessage.value = '登入成功！正在跳轉...';

        // 執行登入成功回調
        if (onLoginSuccess && data.user) {
            await onLoginSuccess(data.user);
        }

        // 延遲跳轉
        setTimeout(() => {
            router.push(successRedirectRoute);
        }, 1000);
    };

    /**
     * 檢查並處理 OAuth 回調
     */
    const checkAuthCallback = async (): Promise<User | null> => {
        try {
            // 檢查 URL 中是否有 OAuth 回調參數
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const errorParam = urlParams.get('error');
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');

            // 處理錯誤參數
            if (errorParam) {
                error.value = `登入失敗: ${errorParam}`;
                cleanupUrlParams();
                return null;
            }

            // 如果有 code 或 access_token，表示是 OAuth 回調
            if (code || accessToken) {
                infoMessage.value = '正在處理登入回調...';
            }

            // 獲取當前 session
            let session: Session | null = null;
            let sessionError: Error | null = null;
            try {
                const sessionResult = await withTokenRefresh(() => supabase.auth.getSession());
                session = sessionResult.data?.session ?? null;
                sessionError = sessionResult.error;
            } catch (err) {
                console.error(`[${appName}] 獲取 session 時發生錯誤:`, err);
                if (isTokenExpiredError(err)) {
                    const refreshedSession = await handleTokenExpiredError(err);
                    if (!refreshedSession) {
                        if (code || accessToken) {
                            error.value = '登入已過期，請重新登入';
                        }
                        return null;
                    }
                    session = refreshedSession;
                    sessionError = null;
                } else {
                    sessionError = err instanceof Error ? err : new Error(String(err));
                }
            }

            if (sessionError) {
                console.error(`[${appName}] 獲取 session 失敗:`, sessionError);
                if (code || accessToken) {
                    error.value = `登入失敗: ${sessionError.message || '無法建立 session'}`;
                }
                return null;
            }

            if (session?.user) {
                user.value = session.user;

                // 清理 URL 參數
                cleanupUrlParams();

                // 執行 session 檢查後的處理
                if (afterSessionCheck) {
                    await afterSessionCheck(session);
                }

                infoMessage.value = '登入成功！正在跳轉...';

                // 執行登入成功回調
                if (onLoginSuccess) {
                    await onLoginSuccess(session.user);
                }

                // 延遲跳轉，讓用戶看到成功訊息
                setTimeout(() => {
                    router.push(successRedirectRoute);
                }, 1000);

                return session.user;
            }

            return null;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '處理登入失敗';
            console.error(`[${appName}] 檢查認證回調失敗:`, err);
            error.value = errorMessage;
            infoMessage.value = '';
            return null;
        }
    };

    /**
     * 檢查當前登入狀態
     */
    const checkLoginStatus = async (): Promise<User | null> => {
        try {
            const sessionResult = await withTokenRefresh(() => supabase.auth.getSession());
            const session = sessionResult.data?.session;

            if (session?.user) {
                user.value = session.user;
                return session.user;
            }
            return null;
        } catch (err) {
            console.error(`[${appName}] 檢查登入狀態失敗:`, err);
            if (isTokenExpiredError(err)) {
                const refreshedSession = await handleTokenExpiredError(err);
                if (refreshedSession?.user) {
                    user.value = refreshedSession.user;
                    return refreshedSession.user;
                }
            }
            return null;
        }
    };

    /**
     * 登出
     */
    const signOut = async (): Promise<void> => {
        try {
            isLoading.value = true;
            error.value = null;

            const { error: signOutError } = await supabase.auth.signOut();

            if (signOutError) throw signOutError;

            user.value = null;

            // 跳轉到登入頁
            router.push('/login');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '登出失敗';
            console.error(`[${appName}] 登出失敗:`, err);
            error.value = errorMessage;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * 清理 URL 參數
     */
    const cleanupUrlParams = (): void => {
        if (window.history && window.history.replaceState) {
            // 清理 query 參數
            const cleanUrl = window.location.pathname + window.location.hash.split('?')[0];
            window.history.replaceState({}, document.title, cleanUrl);
        }
    };

    /**
     * 監聽認證狀態變化（透過 authService 統一管理）
     */
    const setupAuthListener = (): AuthSubscription => {
        // 使用 authService 的訂閱機制
        const unsubscribe = authService.onAuthStateChange((event: string, authUser: unknown) => {
            const typedAuthUser = authUser as { supabaseUser?: User } | User | null;

            if (event === 'TOKEN_REFRESHED' && typedAuthUser) {
                user.value = 'supabaseUser' in typedAuthUser
                    ? typedAuthUser.supabaseUser ?? null
                    : typedAuthUser as User;
            } else if (event === 'SIGNED_IN' && typedAuthUser) {
                user.value = 'supabaseUser' in typedAuthUser
                    ? typedAuthUser.supabaseUser ?? null
                    : typedAuthUser as User;
            } else if (event === 'SIGNED_OUT') {
                user.value = null;
                error.value = null;
                infoMessage.value = '';
            }
        });

        // 回傳一個與 Supabase subscription 相容的物件
        return { unsubscribe };
    };

    /**
     * 初始化認證
     */
    const initAuth = async (): Promise<AuthSubscription> => {
        // 先檢查 OAuth 回調
        await checkAuthCallback();

        // 設置認證狀態監聽器
        const subscription = setupAuthListener();

        return subscription;
    };

    return {
        // 狀態
        isLoading,
        error,
        infoMessage,
        user,

        // 方法
        signInWithGoogle,
        signOut,
        checkLoginStatus,
        checkAuthCallback,
        initAuth,
        cleanupUrlParams
    };
}

export default useSupabaseAuth;
